/**
 * Display formatting.
 *
 * Money and hours arrive as fixed-scale decimal STRINGS (`"1234.5000"`, `"7.50"`), never
 * as JS numbers — see `@shared/types/primitives`. These helpers parse for *display only*.
 * Nothing here ever feeds a value back into a request: arithmetic on money happens on the
 * server, in BigInt.
 *
 * `null` and `undefined` render as an em dash. A missing figure and a zero figure are not
 * the same thing and must not look the same.
 */
import type { Hours, ISODate, ISODateTime, Money, Rate } from '@shared/types';

const ORG_TIMEZONE = 'America/New_York';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const currencyWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const decimal = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const integer = new Intl.NumberFormat('en-US');

export const EM_DASH = '—';

export function formatMoney(value: Money | null | undefined, options?: { whole?: boolean }): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return EM_DASH;
  return options?.whole === true ? currencyWhole.format(parsed) : currency.format(parsed);
}

/** Compact money for KPI tiles: `$1.2M`, `$84.5k`. Never used where a figure is reconciled. */
export function formatMoneyCompact(value: Money | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return EM_DASH;
  const sign = parsed < 0 ? '-' : '';
  const magnitude = Math.abs(parsed);
  if (magnitude >= 1_000_000) return `${sign}$${(magnitude / 1_000_000).toFixed(1)}M`;
  if (magnitude >= 1_000) return `${sign}$${(magnitude / 1_000).toFixed(1)}k`;
  return currencyWhole.format(parsed);
}

export function formatHours(value: Hours | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return EM_DASH;
  return decimal.format(parsed);
}

/** Hours with the unit, for totals rows: `37.50 h`. */
export function formatHoursWithUnit(value: Hours | null | undefined): string {
  const formatted = formatHours(value);
  return formatted === EM_DASH ? EM_DASH : `${formatted} h`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH;
  return integer.format(value);
}

/** A `Rate` (6dp decimal string, `"0.062000"`) as a percentage: `6.20%`. */
export function formatRatePercent(value: Rate | null | undefined, fractionDigits = 2): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return EM_DASH;
  return `${(parsed * 100).toFixed(fractionDigits)}%`;
}

/** A ratio like a compa-ratio (`"1.020000"`) shown as `1.02` — NOT a percentage. */
export function formatRatio(value: Rate | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return EM_DASH;
  return parsed.toFixed(2);
}

export function formatPercent(value: number | null | undefined, fractionDigits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH;
  return `${value.toFixed(fractionDigits)}%`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dates
// ─────────────────────────────────────────────────────────────────────────────

/**
 * An `ISODate` is a calendar date with no time. Parsing it with `new Date('2026-08-03')`
 * gives UTC midnight, which in `America/New_York` is the evening of 2 August — the
 * classic off-by-one that makes a Monday timesheet look like a Sunday one. So the parts
 * are read from the string directly and a LOCAL date is constructed.
 */
export function parseISODate(value: ISODate): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

const dateShort = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const dateMedium = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
const dateLong = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});
const dateTimeMedium = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: ORG_TIMEZONE,
});
const timeOnly = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  timeZone: ORG_TIMEZONE,
});

export function formatDate(value: ISODate | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  return dateMedium.format(parseISODate(value));
}

export function formatDateShort(value: ISODate | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  return dateShort.format(parseISODate(value));
}

export function formatDateLong(value: ISODate | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  return dateLong.format(parseISODate(value));
}

export function formatDateTime(value: ISODateTime | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? EM_DASH : dateTimeMedium.format(parsed);
}

export function formatTime(value: ISODateTime | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? EM_DASH : timeOnly.format(parsed);
}

export function formatDateRange(from: ISODate, to: ISODate): string {
  const start = parseISODate(from);
  const end = parseISODate(to);
  if (start.getFullYear() === end.getFullYear()) {
    return `${dateShort.format(start)} – ${dateMedium.format(end)}`;
  }
  return `${dateMedium.format(start)} – ${dateMedium.format(end)}`;
}

/** "3 days ago", "in 2 hours". Used for SLA and ageing columns. */
export function formatRelative(value: ISODateTime | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) return EM_DASH;
  const deltaSeconds = Math.round((parsed - Date.now()) / 1000);
  const magnitude = Math.abs(deltaSeconds);
  const relative = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
  if (magnitude < 60) return relative.format(deltaSeconds, 'second');
  if (magnitude < 3_600) return relative.format(Math.round(deltaSeconds / 60), 'minute');
  if (magnitude < 86_400) return relative.format(Math.round(deltaSeconds / 3_600), 'hour');
  if (magnitude < 2_592_000) return relative.format(Math.round(deltaSeconds / 86_400), 'day');
  if (magnitude < 31_536_000) return relative.format(Math.round(deltaSeconds / 2_592_000), 'month');
  return relative.format(Math.round(deltaSeconds / 31_536_000), 'year');
}

/** Today, as an `ISODate` in the organisation's timezone. */
export function todayISO(): ISODate {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ORG_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return parts;
}

export function addDaysISO(value: ISODate, days: number): ISODate {
  const date = parseISODate(value);
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function daysBetween(from: ISODate, to: ISODate): number {
  const start = parseISODate(from).getTime();
  const end = parseISODate(to).getTime();
  return Math.round((end - start) / 86_400_000);
}

/** Turns SCREAMING_SNAKE codes into readable text when no lookup label is available. */
export function humanise(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  return value
    .toLowerCase()
    .split(/[_\-\s]+/)
    .map((word) => (word.length === 0 ? word : `${word[0]?.toUpperCase() ?? ''}${word.slice(1)}`))
    .join(' ');
}

export function initialsOf(name: string | null | undefined): string {
  if (name === null || name === undefined || name.trim() === '') return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
